import type { SERVICE, ServiceType } from '@musinsa/packages-device-detection';
import { getCdnUrl } from '../../common/utils';

export type AgreementType = 'terms' | 'policy';
export type AgreementBrand = Exclude<ServiceType, typeof SERVICE.UNKNOWN>;
// = 'MUSINSA' | '29CM'

// NOTE: computed property keys + as const 조합은 TypeScript 추론 실패 가능
// → 리터럴 string 직접 사용
const AGREEMENT_VERSIONS = {
  terms: {
    unified: ['20260629-v2', '20260330-v2'] as readonly string[],
    MUSINSA: ['20260101', '20250401', '20240401', '20230629', '20230518', '20230306'],
    '29CM': ['20260101', '20251023'],
  },
  policy: {
    unified: [] as readonly string[],
    MUSINSA: ['20240401', '20230629', '20230306'],
    '29CM': ['20251023'],
  },
} as const;

// CDN 경로용 소문자 변환 (내부 전용)
// 'MUSINSA' → 'musinsa', '29CM' → '29cm'
const toBrandPath = (brand: AgreementBrand): string => brand.toLowerCase();

// 공통 버전 체이닝: unified → brand 순
// current 없으면 최신 버전(unified[0] ?? brand[0])으로 resolve 후 동일 로직 처리
export const getPreviousVersion = (
  type: AgreementType,
  brand: AgreementBrand,
  current?: string,
): string | null => {
  const unified: string[] = [...AGREEMENT_VERSIONS[type].unified];
  const brandVersions: string[] = [...AGREEMENT_VERSIONS[type][brand]];

  const resolvedCurrent = current ?? unified[0] ?? brandVersions[0];
  if (!resolvedCurrent) return null;

  const unifiedIdx = unified.indexOf(resolvedCurrent);
  if (unifiedIdx !== -1) {
    return unified[unifiedIdx + 1] ?? brandVersions[0] ?? null;
  }

  const brandIdx = brandVersions.indexOf(resolvedCurrent);
  return brandVersions[brandIdx + 1] ?? null;
};

// 공통 CDN 경로 빌더
// NOTE: local 환경(NEXT_PUBLIC_SERVICE_ENV === 'local')에서는 CDN 대신
// Next.js public/ 폴더를 직접 참조한다.
export const getAgreementHtmlPath = (
  type: AgreementType,
  brand: AgreementBrand,
  version?: string,
): string => {
  const ver =
    version ??
    (AGREEMENT_VERSIONS[type].unified[0] as string | undefined) ??
    (AGREEMENT_VERSIONS[type][brand][0] as string | undefined);
  if (!ver) throw new Error(`No version found for ${type}/${brand}`);

  // unified 버전은 브랜드 구분 없이 unified/ 경로 사용
  const isUnified = (AGREEMENT_VERSIONS[type].unified as readonly string[]).includes(ver);
  const dirPath = isUnified ? `unified/${type}` : `${toBrandPath(brand)}/${type}`;

  // NOTE: local 환경에서는 CDN 없이 Next.js public/ 폴더를 직접 참조한다.
  if (process.env.NEXT_PUBLIC_SERVICE_ENV === 'local') {
    return `/html/${dirPath}/${ver}.html`;
  }

  return getCdnUrl(`/html/${dirPath}/${ver}.html`);
};

export const AGREEMENT_VERSION_QUERY_KEY = {
  terms: 'termsVer',
  policy: 'policyVer',
} as const;

export const AGREEMENT_UI_TEXT = {
  terms: { title: '서비스 이용약관', prevButton: '이전 이용약관 보기' },
  policy: { title: '서비스 운영정책', prevButton: '이전 운영정책 보기' },
} as const;
